import { useMemo, useEffect, useState, useRef } from 'react';
import groupBy from 'lodash/groupBy';
import remove from 'lodash/remove';
import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import {
  OnChangeFn,
  RowSelectionState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
  TableOptions,
} from '@tanstack/react-table';
import { useVirtualizer, VirtualizerOptions } from '@tanstack/react-virtual';

export type GroupBy<RData> = (row: RData) => string;
export type IsParent<RData> = (row: RData) => boolean;

export type GroupedRow<RData> = RData & {
  child?: boolean;
  parent?: boolean;
};

function groupData<RData>(
  items: RData[],
  _groupBy: GroupBy<RData>,
  isParent: IsParent<RData>
): GroupedRow<RData>[] {
  return Object.entries(groupBy(items, (i: RData) => _groupBy?.(i))).reduce(
    (acc: GroupedRow<RData>[], [groupKey, values]): GroupedRow<RData>[] => {
      if (
        ['undefined', 'null', '', 'false'].includes(groupKey) ||
        values.length === 1
      ) {
        return [...acc, ...values] as GroupedRow<RData>[];
      }

      const [parent] = remove(values, isParent);
      return [
        ...acc,
        ...(parent
          ? [
              {
                ...parent,
                parent: true,
              },
              ...values.map((i) => ({ ...i, child: _groupBy?.(i) })),
            ]
          : values),
      ] as GroupedRow<RData>[];
    },
    []
  );
}

function useGroupedData<RData>({
  data,
  groupBy,
  isParent,
}: {
  data: RData[];
  groupBy?: GroupBy<RData>;
  isParent?: IsParent<RData>;
}) {
  const groupedData = useMemo<GroupedRow<RData>[]>(
    () =>
      groupBy && isParent
        ? groupData<RData>(data, groupBy, isParent)
        : (data as GroupedRow<RData>[]),
    [data, groupBy, isParent]
  );

  return groupedData;
}

const ArrayFromKeys = (keys: any) =>
  Object.entries(keys)
    .filter(([, value]) => value)
    .map(([key]) => key);

function useArraySelection<RData>({
  data,
  getRowId,
  onSelectionModelChange,
  selectionModel,
}: {
  data: RData[];
  getRowId: any;
  onSelectionModelChange: any;
  selectionModel: any;
}) {
  const rowSelection = useMemo<RowSelectionState>(
    () =>
      selectionModel?.reduce(
        (acc: RowSelectionState, id: string) => ({
          ...acc,
          [id.toString()]: true,
        }),
        {}
      ) || {},
    [selectionModel]
  );

  const onRowSelectionChange: OnChangeFn<RowSelectionState> = (rows) => {
    if (onSelectionModelChange) {
      const newRows = typeof rows === 'function' ? rows(rowSelection) : rows;
      onSelectionModelChange(ArrayFromKeys(newRows));
    }
  };

  useEffect(() => {
    if (selectionModel?.length) {
      const consolidated = intersection(selectionModel, data.map(getRowId));
      if (!isEqual(consolidated, selectionModel)) {
        onSelectionModelChange?.(consolidated);
      }
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return { onRowSelectionChange, rowSelection };
}

export interface VirtualTableOptions<RData>
  extends Omit<TableOptions<RData>, 'getCoreRowModel' | 'getSortedRowModel'> {
  disableVirtualization?: boolean;
  virtualizerOptions?: Partial<VirtualizerOptions<any, any>>;
}

function useVirtualTable<RData>({
  getRowId = (_: RData, index: number) => index.toString(),
  state = {},
  disableVirtualization,
  onRowSelectionChange,
  onSortingChange,
  virtualizerOptions = {},
  ...props
}: VirtualTableOptions<RData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable<RData>({
    defaultColumn: {
      minSize: 10,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: onRowSelectionChange || setRowSelection,
    onSortingChange: onSortingChange || setSorting,
    ...props,
    state: {
      rowSelection,
      sorting,
      ...state,
    },
  });

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const { rows } = table.getRowModel();
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 71,
    getScrollElement: () => tableContainerRef.current,
    overscan: 20,
    ...virtualizerOptions,
  });

  const { getTotalSize, getVirtualItems } = rowVirtualizer;

  const virtualRows = getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? getTotalSize() - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const renderRows = disableVirtualization
    ? rows
    : virtualRows.map((virtualRow) => rows[virtualRow.index]);

  return {
    paddingBottom,
    paddingTop,
    rowVirtualizer,
    rows: renderRows,
    table,
    tableContainerRef,
  };
}

export { useGroupedData, useArraySelection, useVirtualTable };
